import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BotidService } from '@shared-angular/services/ragai/botid.service';
import { docSearchSchema } from '@shared/ragai.schemas';
import { firstValueFrom } from 'rxjs';
import { z } from 'zod';
import { CardsManagerService } from './cards-manager.service';
import { OverlayManagerService } from './overlay-manager.service';

export type DocSearchResult = {
  searchResults: {
    chunk?: {
      content: string;
    };
    document?: {
      filename: string;
    };
    documentUrl: string;
  }[];
}

/**
 * Durchsucht Skill-Dokumente per Vektorsuche und stellt Ergebnisse dar (`RAGAI.DOCSEARCH`).
 * @remarks
 * `returnType` in {@link searchAsJson} bestimmt nicht nur das Format, sondern teils auch was zurückgegeben wird:
 * `"json"` liefert die Rohdaten, `"table"`/`"text"` bereits fertig aufbereitete Darstellungen, `"hasresults"`/
 * `"counter"` nur einen Boolean bzw. eine Zahl (nützlich für Bedingungen im Logik-Editor, ohne die vollen
 * Suchergebnisse verarbeiten zu müssen). `minScore` wird als Prozentwert (0-100) entgegengenommen, aber vor dem
 * Backend-Aufruf intern durch 100 geteilt. {@link clickFn} öffnet Trefferdokumente standardmäßig in einem
 * `RAGAI.OVERLAY.html()`-iFrame, kann aber überschrieben werden.
 */
@Injectable({
  providedIn: 'root'
})

export class DocSearchManagerService {

  public beforeItemHtml = `<div class="list-group me-3">`;
  public itemHtml = `
  <a class="list-group-item list-group-item-action docsearch-link" href="{{url}}">
    <i class="ri-file-pdf-2-fill"></i>
    <strong>{{filename}}</strong><br>
    <small>{{title}}</small>
  </a>
`;
  public afterItemHtml = `</div>`;

  constructor(private http: HttpClient, private botIdService: BotidService, private cards: CardsManagerService, private overlay: OverlayManagerService) { }

  public clickFn: (url: string) => void = (url: string) => {
    this.overlay.html("<iframe src=" + url + " class='docsearch-overlay-frame'></iframe>");
  };

  private _renderTemplate(template: string, data: Record<string, string>): string {
    return template.replace(/{{(.*?)}}/g, (_: string, key: string) => {
      return data[key.trim()] ?? "";
    });
  }

  /**
   * Durchsucht Skill-Dokumente und liefert das Ergebnis im gewünschten Format.
   * @param searchString Suchbegriff, oder ein Objekt gemäß `docSearchSchema`.
   * @param skillIds Auf diese Skills beschränken, oder `null` für alle.
   * @param searchCount Maximale Anzahl an Treffern.
   * @param minScore Mindest-Ähnlichkeitswert in Prozent (0-100).
   * @param returnType Ausgabeformat: `"json"`, `"table"`, `"text"`, `"hasresults"` oder `"counter"`.
   * @param searchType Suchmethode, z. B. `"vector"`.
   * @returns Die Suchergebnisse im gewählten Format.
   */
  public async searchAsJson(searchString: string | z.input<typeof docSearchSchema>, skillIds: null | string[] = null, searchCount: number = 5, minScore: number = 0, returnType: string = "json", searchType: string = "vector") {
    const parsed = docSearchSchema.safeParse(searchString);
    if (parsed.success && typeof searchString === 'object') {
      skillIds = parsed.data.skillIds ?? skillIds;
      searchCount = parsed.data.count ?? searchCount;
      minScore = parsed.data.minScore ?? minScore;
      searchString = parsed.data.query;
    }

    const data = await firstValueFrom(this.http.post(`/api/user/docsearch/${this.botIdService.getBotId()}/search`,
      {
        searchString,
        searchCount,
        minScore: minScore / 100,
        skillIds,
        searchType
      }
    )) as DocSearchResult;

    if (returnType === "hasresults") {
      return data.searchResults.length > 0;
    }
    if (returnType === "table") {
      return this.jsonToHtml(data);
    }
    if (returnType === "counter") {
      return data.searchResults.length;
    }
    if (returnType === "text") {
      return data.searchResults.map(r => `${r.document?.filename ?? "Unbekanntes Dokument"}: ${r.chunk?.content ?? "Kein Inhalt"}`).join("\n\n");
    }
    return data;
  }

  /**
   * Rendert Suchergebnisse als HTML-Liste.
   * @param data Die Suchergebnisse.
   * @returns Der gerenderte HTML-String.
   */
  public jsonToHtml(data: DocSearchResult): string {
    let html = this.beforeItemHtml;
    data.searchResults.forEach(result => {
      html += this._renderTemplate(this.itemHtml, {
        title: result.chunk?.content ?? "Kein Titel",
        filename: result.document?.filename ?? "Unbekanntes Dokument",
        url: result.documentUrl
      });
    });
    html += this.afterItemHtml;
    return html;
  }

  /**
   * Durchsucht Skill-Dokumente und zeigt das Ergebnis direkt als HTML-Card an.
   * @param searchString Suchbegriff.
   * @param skillIds Auf diese Skills beschränken, oder `null` für alle.
   * @param searchCount Maximale Anzahl an Treffern.
   * @param minScore Mindest-Ähnlichkeitswert in Prozent (0-100).
   */
  public async searchAsCard(searchString: string, skillIds: null | string[], searchCount: number = 5, minScore: number = 0) {
    const data = (await this.searchAsJson(searchString, skillIds, searchCount, minScore)) as DocSearchResult;
    const html = this.jsonToHtml(data);
    await this.cards.html(html);
  }

  /**
   * Durchsucht Skill-Dokumente und liefert das Ergebnis als HTML-String zurück.
   * @param searchString Suchbegriff.
   * @param skillIds Auf diese Skills beschränken, oder `null` für alle.
   * @param searchCount Maximale Anzahl an Treffern.
   * @param minScore Mindest-Ähnlichkeitswert in Prozent (0-100).
   * @returns Der gerenderte HTML-String.
   */
  public async docSearchResult(searchString: string, skillIds: null | string[], searchCount: number = 5, minScore: number = 0) {
    const data = (await this.searchAsJson(searchString, skillIds, searchCount, minScore)) as DocSearchResult;
    const html = this.jsonToHtml(data);
    return html;
  }
}
